import { copyPendingText, copyTextAsync, copyTextSync } from './clipboard';
import { DEFAULT_USER_SOURCE, USER_SOURCE_MAX_LENGTH, USER_SOURCE_QUERY_KEY } from './const';
import { collectDevice } from './fingerprint';
import { reportAttribution } from './report';
import type { IAttributionDevice, IAttributionOptions, IReportAttributionReq } from './types';

// ---- 模块级单例状态 ----
let tokenPromise: Promise<string> | null = null;
/** 已就绪的 token，供点击时同步写剪贴板 */
let resolvedToken = '';
/** 预采集的设备参数，避免 report() 时重复采集 */
let devicePromise: Promise<IAttributionDevice> | null = null;
/** 记录预采集时的 collectLanIp，取值不同需要重新采集 */
let devicePromiseCollectLanIp = true;

/**
 * 是否在 Docoi App 内打开：App WebView 会在 UA 里注入 `#webBaseInfo{...}#`。
 * 本 SDK 独立分发（含 IIFE 包），不依赖宿主项目的工具函数，这里按 UA 自行判断，
 * 判定规则需与 `@h5-monorepo/shared/utils/app` 的 `isInDocoiApp` 保持一致。
 */
function isInDocoiApp(): boolean {
  try {
    return /#webBaseInfo(.*?[^\\])#/.test(navigator.userAgent.replace(/\s+/g, ''));
  } catch {
    return false;
  }
}

/** 解析来源标识：显式传入 > URL 参数 userSource > 默认值，并按后端约束截断 */
function resolveUserSource(userSource?: string): string {
  let value = (userSource || '').trim();
  if (!value) {
    try {
      value = (new URLSearchParams(window.location.search).get(USER_SOURCE_QUERY_KEY) || '').trim();
    } catch {
      value = '';
    }
  }
  return (value || DEFAULT_USER_SOURCE).slice(0, USER_SOURCE_MAX_LENGTH);
}

/**
 * 初始化：预采集设备参数，不发上报请求（幂等，多次调用只采集一次）。
 * 用于站外场景顶层提前调用：此时业务参数（userSource）往往还不确定，
 * 先把耗时的设备指纹采集（WebRTC 内网 IP、UA 高熵值）跑掉，
 * 等真正调用 report() 时直接复用，省去这部分等待。
 * App 内打开时不需要归因，不触发采集。
 */
export function init(collectLanIp = true): void {
  if (isInDocoiApp()) return;
  if (devicePromise && devicePromiseCollectLanIp === collectLanIp) return;
  devicePromiseCollectLanIp = collectLanIp;
  devicePromise = collectDevice(collectLanIp);
}

/** 触发上报，返回 token（幂等，多次调用只上报一次）；若调用过 init() 直接复用预采集结果 */
function ensureReported(options: IAttributionOptions): Promise<string> {
  if (tokenPromise) return tokenPromise;
  if (isInDocoiApp()) {
    tokenPromise = Promise.resolve('');
    return tokenPromise;
  }

  const { userSource, collectLanIp = true, apiBase } = options;
  const deviceSource =
    devicePromise && devicePromiseCollectLanIp === collectLanIp ? devicePromise : collectDevice(collectLanIp);
  tokenPromise = deviceSource
    .then((device) => {
      const payload: IReportAttributionReq = { user_source: resolveUserSource(userSource), device };
      return reportAttribution(payload, apiBase);
    })
    .then(({ token }) => {
      resolvedToken = token;
      // 上报失败时允许下次调用重试
      if (!token) tokenPromise = null;
      return token;
    })
    .catch(() => {
      tokenPromise = null;
      return '';
    });
  return tokenPromise;
}

/**
 * 上报：触发采集+上报（若调用过 init() 则复用预采集的设备参数），取回 token 后写入剪贴板。
 * 必须在用户点击回调中、任何 await 之前调用，以保证写剪贴板处于用户手势内：
 * - token 已就绪：同步 execCommand 写入，失败再走 Clipboard API
 * - token 未就绪：用 ClipboardItem(Promise) 在手势内先占位，内容就绪后写入
 * 返回是否写入剪贴板成功；不会抛错，不影响后续唤起/下载流程。
 */
export function report(options: IAttributionOptions = {}): Promise<boolean> {
  if (resolvedToken) {
    if (copyTextSync(resolvedToken)) return Promise.resolve(true);
    return copyTextAsync(resolvedToken);
  }
  if (isInDocoiApp()) return Promise.resolve(false);

  const pending = ensureReported(options);
  return copyPendingText(pending).then((ok) => {
    if (ok) return true;
    // 不支持 ClipboardItem(Promise) 时退回：等 token 再写，已脱离手势，部分浏览器会失败
    return pending.then((token) => (token ? copyTextAsync(token) : false));
  });
}

/** 当前已就绪的 token，未就绪或失败时为空 */
export function getAttributionToken(): string {
  return resolvedToken;
}

/** 重置状态，用于来源变化后重新上报（一般不需要调用） */
export function resetAttribution(): void {
  tokenPromise = null;
  resolvedToken = '';
}

/** 清空预采集的设备参数缓存（一般不需要调用） */
export function resetDevicePrewarm(): void {
  devicePromise = null;
}
